import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  FileText,
  CheckCircle2,
  HardDrive,
  Search,
  RefreshCw,
  Upload,
  Download,
  Trash2,
  File,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw,
  Cpu,
  LayoutList,
  X,
  AlertCircle,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService, eventsApi, adminApi, documentsApi } from '../../services/api';
import { IDocumentInfo, IEvent, IApiResponse, DocumentRAGInfo, RAGChunk, AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import RAGProcessingModal from '../../components/RAGProcessing/RAGProcessingModal';

interface DocumentsState {
  documents: IDocumentInfo[];
  events: IEvent[];
  clients: AppUser[];
  loading: boolean;
  uploadModalVisible: boolean;
  textModalVisible: boolean;
  selectedClientId: string | null;
  selectedEventId: string | null;
  selectedDocument: IDocumentInfo | null;
  uploading: boolean;
  documentStats: {
    totalDocuments: number;
    processedDocuments: number;
    totalSize: number;
    remainingDocuments: number;
  };
  page: number;
  limit: number;
  total: number;
  createRAGModalVisible: boolean;
  viewRAGModalVisible: boolean;
  selectedDocumentForRAG: IDocumentInfo | null;
  ragLoading: boolean;
  ragDetails: DocumentRAGInfo | null;
  ragChunks: RAGChunk[];
  ragPage: number;
  ragLimit: number;
  ragTotal: number;
  isReprocessing: boolean;
  ragForm: {
    strategy: 'fixed' | 'semantic' | 'paragraph';
    chunkSize: number;
    chunkOverlap: number;
  };
  realtimeRAGModalVisible: boolean;
  useRealtimeRAG: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  pdf: 'bg-red-50 text-red-700 border border-red-200',
  docx: 'bg-blue-50 text-blue-700 border border-blue-200',
  txt: 'bg-gray-100 text-gray-700 border border-gray-200',
  xlsx: 'bg-green-50 text-green-700 border border-green-200',
  xls: 'bg-green-50 text-green-700 border border-green-200',
};

const STATUS_CONFIG = {
  processed: { label: 'Processed', className: 'bg-green-50 text-green-700 border border-green-200' },
  processing: { label: 'Processing', className: 'bg-yellow-50 text-yellow-700 border border-yellow-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border border-red-200' },
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-600 border border-gray-200' },
};

const PAGE_SIZE = 10;

const Documents: React.FC = () => {
  const { isAdmin } = useAuth();
  const [state, setState] = useState<DocumentsState>({
    documents: [],
    events: [],
    clients: [],
    loading: false,
    uploadModalVisible: false,
    textModalVisible: false,
    selectedClientId: null,
    selectedEventId: null,
    selectedDocument: null,
    uploading: false,
    documentStats: { totalDocuments: 0, processedDocuments: 0, totalSize: 0, remainingDocuments: 0 },
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    createRAGModalVisible: false,
    viewRAGModalVisible: false,
    selectedDocumentForRAG: null,
    ragLoading: false,
    ragDetails: null,
    ragChunks: [],
    ragPage: 1,
    ragLimit: 10,
    ragTotal: 0,
    isReprocessing: false,
    ragForm: { strategy: 'semantic', chunkSize: 1000, chunkOverlap: 200 },
    realtimeRAGModalVisible: false,
    useRealtimeRAG: true,
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [batchRAGLoading, setBatchRAGLoading] = useState(false);
  const [docToDelete, setDocToDelete] = useState<IDocumentInfo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [detailsDoc, setDetailsDoc] = useState<IDocumentInfo | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  type FilePhase = 'pending' | 'uploading' | 'rag-processing' | 'done' | 'upload-failed' | 'rag-failed';
  interface FileUploadStatus { phase: FilePhase; error?: string; }
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAdmin) fetchClients();
    else fetchEvents();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin && state.selectedClientId) fetchEvents();
    else if (!isAdmin) fetchEvents();
  }, [state.selectedClientId, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') || '1', 10);
    const limit = parseInt(params.get('limit') || String(PAGE_SIZE), 10);
    setState(prev => ({ ...prev, page: isNaN(page) ? 1 : page, limit: isNaN(limit) ? PAGE_SIZE : limit }));
  }, [location.search]);

  const fetchClients = async () => {
    try {
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setState(prev => ({ ...prev, clients: response.data.data?.users || [] }));
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && state.selectedClientId) params.clientId = state.selectedClientId;
      const response = await eventsApi.getAll(params);
      const events = response.data.data || [];
      const firstEventId = events.length > 0 ? events[0]._id : null;
      setState(prev => ({ ...prev, events, selectedEventId: firstEventId }));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (state.selectedEventId) {
      const params = new URLSearchParams(location.search);
      params.set('page', '1');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocuments = useCallback(async (eventId: string, page: number, limit: number, search?: string) => {
    if (!eventId) {
      setState(prev => ({ ...prev, documents: [], total: 0, loading: false }));
      return;
    }
    setState(prev => ({ ...prev, loading: true }));
    try {
      const params: any = { page, limit };
      if (search && search.trim()) params.search = search.trim();
      const response: IApiResponse<IDocumentInfo[]> = await apiService.get(
        `/documents/event/${eventId}`,
        { params }
      );
      if (response.success && response.data) {
        const counts = response.pagination?.counts;
        setState(prev => ({
          ...prev,
          documents: response.data || [],
          total: response.pagination?.total || prev.total,
          page: response.pagination?.page || prev.page,
          limit: response.pagination?.limit || prev.limit,
          loading: false,
        }));
        calculateStats(response.data || [], response.pagination?.total, counts);
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (state.selectedEventId) {
      fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
    } else {
      setState(prev => ({ ...prev, documents: [], total: 0 }));
    }
  }, [state.selectedEventId, state.page, state.limit, debouncedSearch, fetchDocuments]);

  const calculateStats = (
    documents: IDocumentInfo[],
    totalFromServer?: number,
    counts?: { total: number; completed: number; pending: number; processing: number; failed: number }
  ) => {
    const totalDocuments = counts?.total ?? (typeof totalFromServer === 'number' ? totalFromServer : documents.length);
    const processedDocuments = counts?.completed ?? documents.filter(d => d.isProcessed).length;
    const remainingDocuments = counts ? (counts.pending + counts.processing) : Math.max(0, totalDocuments - processedDocuments);
    const totalSize = documents.reduce((sum, doc) => sum + (doc.fileSize || 0), 0);
    setState(prev => ({ ...prev, documentStats: { totalDocuments, processedDocuments, totalSize, remainingDocuments } }));
  };

  const handleClientChange = (clientId: string | null) => {
    setState(prev => ({ ...prev, selectedClientId: clientId, selectedEventId: null, page: 1 }));
    const params = new URLSearchParams(location.search);
    params.set('page', '1');
    navigate({ search: params.toString() });
  };

  const handleEventChange = (eventId: string) => {
    setState(prev => ({ ...prev, selectedEventId: eventId, page: 1 }));
    const params = new URLSearchParams(location.search);
    params.set('page', '1');
    navigate({ search: params.toString() });
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    setDeleting(true);
    try {
      const response: IApiResponse<any> = await apiService.delete(`/documents/${docToDelete._id}`);
      if (response.success) {
        toast.success('Document deleted successfully');
        setDocToDelete(null);
        if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
      } else {
        toast.error(response.message || 'Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const handleReprocess = async (documentId: string) => {
    try {
      const response: IApiResponse<any> = await apiService.post(`/documents/${documentId}/reprocess`);
      if (response.success) {
        toast.success('Document reprocessing started');
        if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
      } else {
        toast.error(response.message || 'Failed to reprocess document');
      }
    } catch (error) {
      toast.error('Failed to reprocess document');
    }
  };

  const handleViewText = (document: IDocumentInfo) => {
    setState(prev => ({ ...prev, selectedDocument: document, textModalVisible: true }));
  };

  const openCreateRAG = async (document: IDocumentInfo, isReprocess = false) => {
    if (!document.isProcessed) { toast.warning('Please wait until the document is processed.'); return; }
    if (isReprocess && document.isRAGProcessed) {
      try {
        const resp: IApiResponse<DocumentRAGInfo> = await apiService.get(`/documents/${document._id}/rag`);
        if (resp.success && resp.data) {
          setState(prev => ({
            ...prev,
            selectedDocumentForRAG: document,
            createRAGModalVisible: !prev.useRealtimeRAG,
            realtimeRAGModalVisible: prev.useRealtimeRAG,
            isReprocessing: isReprocess,
            ragForm: { strategy: 'semantic', chunkSize: resp.data!.chunkSize, chunkOverlap: resp.data!.chunkOverlap },
          }));
          return;
        }
      } catch (err) { console.error('Error loading RAG settings:', err); }
    }
    setState(prev => ({
      ...prev,
      selectedDocumentForRAG: document,
      createRAGModalVisible: !prev.useRealtimeRAG,
      realtimeRAGModalVisible: prev.useRealtimeRAG,
      isReprocessing: isReprocess,
      ragForm: prev.ragForm,
    }));
  };

  const submitCreateRAG = async () => {
    if (!state.selectedDocumentForRAG) return;
    setState(prev => ({ ...prev, ragLoading: true }));
    try {
      const payload = { strategy: state.ragForm.strategy, chunkSize: state.ragForm.chunkSize, chunkOverlap: state.ragForm.chunkOverlap, reprocess: state.isReprocessing };
      const resp: IApiResponse<any> = await apiService.post(`/documents/${state.selectedDocumentForRAG._id}/rag/create`, payload);
      if (resp.success) {
        if (resp.data?.alreadyExists) toast.warning('RAG already exists for this document');
        else if (state.isReprocessing) toast.success('RAG reprocessed successfully');
        else toast.success('RAG created successfully');
        setState(prev => ({ ...prev, createRAGModalVisible: false }));
        if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
      } else {
        toast.error(resp.message || 'Failed to create RAG');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create RAG');
    } finally {
      setState(prev => ({ ...prev, ragLoading: false }));
    }
  };

  const openViewRAG = async (document: IDocumentInfo) => {
    setState(prev => ({ ...prev, ragLoading: true, viewRAGModalVisible: true, selectedDocumentForRAG: document, ragDetails: null, ragChunks: [], ragPage: 1 }));
    try {
      const details: IApiResponse<DocumentRAGInfo> = await apiService.get(`/documents/${document._id}/rag`);
      if (!details.success || !details.data) {
        toast.info('No RAG available for this document yet.');
        setState(prev => ({ ...prev, viewRAGModalVisible: false, ragLoading: false }));
        return;
      }
      await loadRAGChunks(document._id, 1, state.ragLimit, details.data);
    } catch (err: any) {
      toast.info(err?.response?.data?.message || 'No RAG available for this document yet.');
      setState(prev => ({ ...prev, viewRAGModalVisible: false }));
    } finally {
      setState(prev => ({ ...prev, ragLoading: false }));
    }
  };

  const loadRAGChunks = async (documentId: string, page: number, limit: number, existingDetails?: DocumentRAGInfo | null) => {
    setState(prev => ({ ...prev, ragLoading: true }));
    try {
      const resp: IApiResponse<{ chunks: RAGChunk[]; ragInfo: DocumentRAGInfo }> = await apiService.get(`/documents/${documentId}/rag/chunks`, { params: { page, limit } });
      const data = resp.data as any;
      setState(prev => ({
        ...prev,
        ragChunks: data?.chunks || [],
        ragDetails: existingDetails || data?.ragInfo || prev.ragDetails,
        ragPage: page,
        ragLimit: limit,
        ragTotal: existingDetails?.totalChunks || data?.ragInfo?.totalChunks || 0,
      }));
    } catch (err) {
      toast.error('Failed to load RAG chunks');
    } finally {
      setState(prev => ({ ...prev, ragLoading: false }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleBatchRAG = async (mode: 'selected' | 'all_missing') => {
    if (!state.selectedEventId) { toast.error('Select an event first'); return; }
    if (mode === 'selected' && selectedRowKeys.size === 0) { toast.error('Select at least one document'); return; }
    setBatchRAGLoading(true);
    try {
      const payload: any = { eventId: state.selectedEventId };
      if (mode === 'all_missing') payload.mode = 'all_missing';
      else payload.documentIds = Array.from(selectedRowKeys);
      const res = await apiService.rag.createRAGBatch(payload);
      const payloadData = (res as any)?.data?.data ?? (res as any)?.data ?? {};
      const created = payloadData.created ?? 0;
      const skipped = (payloadData.skipped_already_exists ?? 0) + (payloadData.skipped_not_processed ?? 0);
      const failed = payloadData.failed ?? 0;
      toast.success(`Batch RAG: ${created} created, ${skipped} skipped, ${failed} failed`);
      setSelectedRowKeys(new Set());
      if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Batch RAG failed');
    } finally {
      setBatchRAGLoading(false);
    }
  };

  // Upload
  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const valid = Array.from(files).filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!['pdf', 'docx', 'txt', 'xlsx', 'xls'].includes(ext || '')) {
        toast.error(`${f.name}: unsupported file type`);
        return false;
      }
      if (f.size > 100 * 1024 * 1024) {
        toast.error(`${f.name}: must be smaller than 100MB`);
        return false;
      }
      return true;
    });
    setUploadFiles(prev => [...prev, ...valid]);
  };

  const setFilePhase = (index: number, phase: FileUploadStatus['phase'], error?: string) => {
    setFileStatuses(prev => prev.map((s, i) => i === index ? { phase, error } : s));
  };

  const handleUploadSubmit = async () => {
    if (!state.selectedEventId) { toast.warning('Please select an event first'); return; }
    if (uploadFiles.length === 0) { toast.warning('Please select files to upload'); return; }

    setFileStatuses(uploadFiles.map(() => ({ phase: 'pending' })));
    setState(prev => ({ ...prev, uploading: true }));

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      setFilePhase(i, 'uploading');
      try {
        const res = await documentsApi.upload(state.selectedEventId, file);
        const docId = res.data?.data?.document?._id;

        if (docId) {
          setFilePhase(i, 'rag-processing');
          try {
            await apiService.rag.createForDocument(docId, { strategy: 'semantic', chunkSize: 1000, chunkOverlap: 200 });
            setFilePhase(i, 'done');
          } catch (ragErr: any) {
            const msg = ragErr?.response?.data?.message || ragErr?.message || 'RAG creation failed';
            setFilePhase(i, 'rag-failed', msg);
          }
        } else {
          setFilePhase(i, 'done');
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Upload failed';
        setFilePhase(i, 'upload-failed', msg);
      }
    }

    setState(prev => ({ ...prev, uploading: false }));
    if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
  };

  const closeUploadModal = () => {
    setState(prev => ({ ...prev, uploadModalVisible: false }));
    setUploadFiles([]);
    setFileStatuses([]);
  };

  const allFilesDone = fileStatuses.length > 0 && fileStatuses.every(s =>
    s.phase === 'done' || s.phase === 'upload-failed' || s.phase === 'rag-failed'
  );

  const toggleRow = (id: string) => {
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRowKeys.size === state.documents.length && state.documents.length > 0) {
      setSelectedRowKeys(new Set());
    } else {
      setSelectedRowKeys(new Set(state.documents.map(d => d._id)));
    }
  };

  const goToPage = (p: number) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(p));
    params.set('limit', String(state.limit));
    navigate({ search: params.toString() });
  };

  const totalPages = Math.max(1, Math.ceil((state.total || state.documentStats.totalDocuments) / state.limit));

  const getStatusConfig = (doc: IDocumentInfo) => {
    if (doc.processingStatus === 'failed') return STATUS_CONFIG.failed;
    if (doc.processingStatus === 'pending') return STATUS_CONFIG.pending;
    if (doc.isProcessed) return STATUS_CONFIG.processed;
    return STATUS_CONFIG.processing;
  };

  const getTypeClass = (fileType: string) => {
    return TYPE_COLORS[fileType?.toLowerCase()] || 'bg-gray-100 text-gray-600 border border-gray-200';
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Documents</h1>
        <p className="text-sm text-gray-500 mt-1">Manage and process knowledge base documents</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Documents</p>
              <p className="text-3xl font-semibold text-gray-900">{state.documentStats.totalDocuments}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Processed Documents</p>
              <p className="text-3xl font-semibold text-gray-900">{state.documentStats.processedDocuments}</p>
            </div>
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Size</p>
              <p className="text-3xl font-semibold text-gray-900">{formatFileSize(state.documentStats.totalSize)}</p>
            </div>
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Processing alert */}
      {state.documentStats.remainingDocuments > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            <span className="font-medium">Processing in progress</span> — {state.documentStats.remainingDocuments} document(s) are still being processed.
          </p>
        </div>
      )}

      {/* Documents Table Card */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900">Documents Management</h2>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search (client-side on visible page) */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-52"
                />
              </div>

              {/* Client filter (admin) */}
              {isAdmin && (
                <select
                  value={state.selectedClientId || ''}
                  onChange={(e) => handleClientChange(e.target.value || null)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
                >
                  <option value="">All Clients</option>
                  {state.clients.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              )}

              {/* Event filter */}
              <select
                value={state.selectedEventId || ''}
                onChange={(e) => handleEventChange(e.target.value)}
                disabled={isAdmin && !state.selectedClientId}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!isAdmin && <option value="">Select Knowledge Assistant</option>}
                {state.events.map(ev => (
                  <option key={ev._id} value={ev._id}>{ev.name}</option>
                ))}
              </select>

              {/* Refresh */}
              <button
                onClick={() => { if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch); }}
                disabled={!state.selectedEventId}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              {/* Upload */}
              <button
                onClick={() => {
                  if (!state.selectedEventId) { toast.warning('Please select an event first'); return; }
                  setUploadFiles([]);
                  setState(prev => ({ ...prev, uploadModalVisible: true }));
                }}
                disabled={!state.selectedEventId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                Upload Documents
              </button>
            </div>
          </div>

          {/* Bulk actions — RAG creation is now automatic on upload */}
          {/* <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => handleBatchRAG('selected')}
              disabled={!state.selectedEventId || selectedRowKeys.size === 0 || batchRAGLoading}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors flex items-center gap-2 ${
                selectedRowKeys.size > 0 && state.selectedEventId
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
            >
              <Cpu className="w-3.5 h-3.5" />
              Create RAG for selected ({selectedRowKeys.size})
            </button>
            <button
              onClick={() => handleBatchRAG('all_missing')}
              disabled={!state.selectedEventId || batchRAGLoading}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Cpu className="w-3.5 h-3.5" />
              Create RAG for all missing
            </button>
          </div> */}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={state.documents.length > 0 && selectedRowKeys.size === state.documents.length}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {state.loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-400">Loading...</td>
                </tr>
              ) : state.documents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-gray-500 text-sm">No documents found</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {!state.selectedEventId
                          ? 'Select a Knowledge Assistant to view documents'
                          : debouncedSearch
                          ? `No documents match "${debouncedSearch}"`
                          : 'Upload a document to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                state.documents.map((doc) => {
                  const statusCfg = getStatusConfig(doc);
                  return (
                    <tr key={doc._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedRowKeys.has(doc._id)}
                          onChange={() => toggleRow(doc._id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 whitespace-nowrap max-w-[200px] truncate" title={doc.originalName}>
                            {doc.originalName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeClass(doc.fileType)}`}>
                          {doc.fileType?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatFileSize(doc.fileSize)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.className}`}
                            title={doc.processingStatus === 'failed' && doc.processingError ? doc.processingError : undefined}
                          >
                            {statusCfg.label}
                          </span>
                          {doc.isRAGProcessed ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                              RAG Ready
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                              No RAG
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        {doc.keywords && doc.keywords.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {doc.keywords.slice(0, 3).map((kw, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                                {kw}
                              </span>
                            ))}
                            {doc.keywords.length > 3 && (
                              <span className="text-xs text-blue-600 font-medium self-center">
                                +{doc.keywords.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No keywords</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(doc.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          {/* Row details */}
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => setDetailsDoc(doc)}
                                title="View details"
                                className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {/* View text */}
                              <button
                                onClick={() => handleViewText(doc)}
                                disabled={!doc.isProcessed}
                                title="View extracted text"
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {/* Download */}
                          <button
                            onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5004/api'}/documents/${doc._id}/download`, '_blank')}
                            title="Download"
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <>
                              {/* Reprocess */}
                              <button
                                onClick={() => handleReprocess(doc._id)}
                                title="Reprocess"
                                className="p-1.5 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                              {/* RAG manual create/reprocess — disabled; RAG is now auto-created on upload */}
                              {/* {!doc.isRAGProcessed ? (
                                <button
                                  onClick={() => openCreateRAG(doc, false)}
                                  disabled={!doc.isProcessed}
                                  title="Create RAG"
                                  className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Cpu className="w-4 h-4" />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => openCreateRAG(doc, true)}
                                    title="Reprocess RAG"
                                    className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                  >
                                    <Cpu className="w-4 h-4" />
                                  </button>
                                </>
                              )} */}
                              {/* View RAG chunks — admin only */}
                              {doc.isRAGProcessed && (
                                <button
                                  onClick={() => openViewRAG(doc)}
                                  title="View RAG details"
                                  className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                >
                                  <LayoutList className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                          {/* Delete */}
                          <button
                            onClick={() => setDocToDelete(doc)}
                            title="Delete"
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {state.documentStats.totalDocuments === 0
                ? '0 documents'
                : `${(state.page - 1) * state.limit + 1}–${Math.min(state.page * state.limit, state.documentStats.totalDocuments)} of ${state.documentStats.totalDocuments} documents`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(state.page - 1)}
                disabled={state.page <= 1}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = totalPages <= 5 ? i + 1 : Math.max(1, state.page - 2) + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => goToPage(p)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${p === state.page ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >{p}</button>
                );
              })}
              <button
                onClick={() => goToPage(state.page + 1)}
                disabled={state.page >= totalPages}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
              <span className="ml-2 text-sm text-gray-500">{state.limit} / page</span>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {state.uploadModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => { if (!state.uploading) closeUploadModal(); }}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
              {!state.uploading && (
                <button onClick={closeUploadModal} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>
            <div className="p-6 space-y-4">
              {/* Event notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                <span className="font-medium">Uploading to:</span> {state.events.find(e => e._id === state.selectedEventId)?.name || 'Unknown Knowledge Assistant'}
              </div>

              {/* Selection phase: drop zone + file list */}
              {fileStatuses.length === 0 && (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                    onDragLeave={() => setIsDraggingOver(false)}
                    onDrop={(e) => { e.preventDefault(); setIsDraggingOver(false); handleFilesSelected(e.dataTransfer.files); }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">Click or drag files to upload</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, DOCX, TXT, XLSX up to 100MB each</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.txt,.xlsx,.xls"
                      className="hidden"
                      onChange={(e) => handleFilesSelected(e.target.files)}
                    />
                  </div>

                  {uploadFiles.length > 0 && (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {uploadFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2 truncate">
                            <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-gray-700">{f.name}</span>
                          </div>
                          <button onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))} className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Processing phase: per-file progress */}
              {fileStatuses.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {uploadFiles.map((f, i) => {
                    const s = fileStatuses[i];
                    return (
                      <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-gray-50 rounded-lg">
                        <div className="mt-0.5 flex-shrink-0">
                          {s?.phase === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-gray-300" />}
                          {s?.phase === 'uploading' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                          {s?.phase === 'rag-processing' && <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />}
                          {s?.phase === 'done' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                          {s?.phase === 'upload-failed' && <X className="w-4 h-4 text-red-500" />}
                          {s?.phase === 'rag-failed' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 truncate">{f.name}</p>
                          <p className={`text-xs mt-0.5 ${
                            s?.phase === 'done' ? 'text-green-600' :
                            s?.phase === 'upload-failed' ? 'text-red-500' :
                            s?.phase === 'rag-failed' ? 'text-amber-600' :
                            s?.phase === 'uploading' ? 'text-blue-500' :
                            s?.phase === 'rag-processing' ? 'text-purple-600' :
                            'text-gray-400'
                          }`}>
                            {s?.phase === 'pending' && 'Waiting…'}
                            {s?.phase === 'uploading' && 'Uploading…'}
                            {s?.phase === 'rag-processing' && 'Creating RAG…'}
                            {s?.phase === 'done' && 'Done'}
                            {s?.phase === 'upload-failed' && (s.error || 'Upload failed')}
                            {s?.phase === 'rag-failed' && `Uploaded — RAG failed: ${s.error || 'unknown error'}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
                {fileStatuses.length === 0 ? (
                  <>
                    <button
                      onClick={closeUploadModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUploadSubmit}
                      disabled={uploadFiles.length === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload {uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}
                    </button>
                  </>
                ) : allFilesDone ? (
                  <button
                    onClick={closeUploadModal}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </button>
                ) : (
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing…
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text View Modal */}
      {state.textModalVisible && state.selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setState(prev => ({ ...prev, textModalVisible: false }))} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 truncate pr-4">Extracted Text — {state.selectedDocument.originalName}</h2>
              <button onClick={() => setState(prev => ({ ...prev, textModalVisible: false }))} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {state.selectedDocument.summary && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Summary</p>
                  <p className="text-sm text-gray-700">{state.selectedDocument.summary}</p>
                </div>
              )}
              {state.selectedDocument.keywords && state.selectedDocument.keywords.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Keywords</p>
                  <div className="flex flex-wrap gap-1.5">
                    {state.selectedDocument.keywords.map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs bg-white border border-gray-200 text-gray-600">{kw}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Extracted Text</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {state.selectedDocument.extractedText || 'No text extracted'}
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
              <button onClick={() => setState(prev => ({ ...prev, textModalVisible: false }))}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Reprocess RAG Modal */}
      {state.createRAGModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setState(prev => ({ ...prev, createRAGModalVisible: false }))} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {state.isReprocessing ? 'Reprocess' : 'Create'} RAG — {state.selectedDocumentForRAG?.originalName}
              </h2>
              <button onClick={() => setState(prev => ({ ...prev, createRAGModalVisible: false }))} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {state.isReprocessing ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                  This will delete existing chunks and recreate RAG. Modify parameters below before reprocessing.
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
                  Configure how the document should be chunked for vector search.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Chunking Strategy</label>
                <select
                  value={state.ragForm.strategy}
                  onChange={(e) => setState(prev => ({ ...prev, ragForm: { ...prev.ragForm, strategy: e.target.value as any } }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="semantic">Semantic (Recommended)</option>
                  <option value="paragraph">Paragraph</option>
                  <option value="fixed">Fixed size</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Chunk Size</label>
                  <input
                    type="number"
                    min={100}
                    max={5000}
                    step={50}
                    value={state.ragForm.chunkSize}
                    onChange={(e) => setState(prev => ({ ...prev, ragForm: { ...prev.ragForm, chunkSize: Number(e.target.value) || 1000 } }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Overlap</label>
                  <input
                    type="number"
                    min={0}
                    max={500}
                    step={10}
                    value={state.ragForm.chunkOverlap}
                    onChange={(e) => setState(prev => ({ ...prev, ragForm: { ...prev.ragForm, chunkOverlap: Number(e.target.value) || 200 } }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
                <button
                  onClick={() => setState(prev => ({ ...prev, createRAGModalVisible: false }))}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitCreateRAG}
                  disabled={state.ragLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  {state.ragLoading ? 'Processing...' : state.isReprocessing ? 'Reprocess RAG' : 'Create RAG'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View RAG Modal */}
      {state.viewRAGModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setState(prev => ({ ...prev, viewRAGModalVisible: false }))} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 truncate pr-4">RAG Details — {state.selectedDocumentForRAG?.originalName}</h2>
              <button onClick={() => setState(prev => ({ ...prev, viewRAGModalVisible: false }))} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {state.ragLoading && (
                <div className="text-center py-4 text-sm text-gray-400">Loading RAG details...</div>
              )}
              {state.ragDetails && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Chunks', value: state.ragDetails.totalChunks },
                    { label: 'Avg Chunk Size', value: state.ragDetails.averageChunkSize },
                    { label: 'Strategy', value: state.ragDetails.chunkingStrategy.toUpperCase() },
                    { label: 'Embedding Model', value: state.ragDetails.embeddingModel },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <p className="text-sm font-semibold text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              )}
              {state.ragChunks.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Chunks</p>
                  <div className="space-y-3">
                    {state.ragChunks.map((chunk) => (
                      <div key={chunk.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1.5">Chunk #{chunk.chunkIndex}</p>
                        <p className="text-sm text-gray-700 line-clamp-3">{chunk.chunkText}</p>
                      </div>
                    ))}
                  </div>
                  {/* RAG chunk pagination */}
                  {state.ragTotal > state.ragLimit && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <button
                        onClick={() => { if (state.selectedDocumentForRAG) loadRAGChunks(state.selectedDocumentForRAG._id, state.ragPage - 1, state.ragLimit); }}
                        disabled={state.ragPage <= 1}
                        className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-600">Page {state.ragPage}</span>
                      <button
                        onClick={() => { if (state.selectedDocumentForRAG) loadRAGChunks(state.selectedDocumentForRAG._id, state.ragPage + 1, state.ragLimit); }}
                        disabled={state.ragPage * state.ragLimit >= state.ragTotal}
                        className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
              <button onClick={() => setState(prev => ({ ...prev, viewRAGModalVisible: false }))}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Row Details Modal */}
      {detailsDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDetailsDoc(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Document Details</h2>
              <button onClick={() => setDetailsDoc(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-4 text-sm">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">File Name</p>
                <p className="text-gray-900 break-words">{detailsDoc.originalName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Type</p>
                  <p className="text-gray-800">{detailsDoc.fileType?.toUpperCase() || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Size</p>
                  <p className="text-gray-800">{formatFileSize(detailsDoc.fileSize)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Processing Status</p>
                  <p className="text-gray-800">{detailsDoc.processingStatus || (detailsDoc.isProcessed ? 'completed' : 'processing')}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">RAG Processed</p>
                  <p className="text-gray-800">{detailsDoc.isRAGProcessed ? 'Yes' : 'No'}</p>
                </div>
              </div>
              {detailsDoc.processingStatus === 'failed' && detailsDoc.processingError && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Processing Error</p>
                  <p className="text-red-600 text-sm">{detailsDoc.processingError}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Keywords</p>
                {detailsDoc.keywords && detailsDoc.keywords.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {detailsDoc.keywords.map((kw, idx) => (
                      <span key={`${detailsDoc._id}-kw-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
                        {kw}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">No keywords</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Uploaded</p>
                  <p className="text-gray-800">{new Date(detailsDoc.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Updated</p>
                  <p className="text-gray-800">{new Date(detailsDoc.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setDetailsDoc(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {docToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDocToDelete(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Delete Document</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium">{docToDelete.originalName}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone and will remove the document from the knowledge base.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setDocToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                No, Keep It
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60">
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Real-Time RAG Processing Modal (keep as-is) */}
      <RAGProcessingModal
        visible={state.realtimeRAGModalVisible}
        document={state.selectedDocumentForRAG}
        onClose={() => setState(prev => ({ ...prev, realtimeRAGModalVisible: false }))}
        onComplete={() => {
          setState(prev => ({ ...prev, realtimeRAGModalVisible: false }));
          if (state.selectedEventId) fetchDocuments(state.selectedEventId, state.page, state.limit, debouncedSearch);
          toast.success('RAG processing completed successfully!');
        }}
        strategy={state.ragForm.strategy}
        chunkSize={state.ragForm.chunkSize}
        chunkOverlap={state.ragForm.chunkOverlap}
        reprocess={state.isReprocessing}
      />
    </div>
  );
};

export default Documents;
